import React, { useEffect, useState } from 'react';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { UserService } from '../Service';
import moment from 'moment';
import PhoneNumber from '../../../../components/inputs/PhoneNumber';
import { VerificationCodeService } from '../../../verificationCode/service';
import { useAppContext } from '../../../../contexts/AppContext';
import { SettingsRow, SettingsSection } from '../../estabelecimento/Form/SettingsGroup';
import { PromptBenefits } from '../../../../components/PromptSheet';
import { Bell, CalendarCheck, CircleCheck, MessageSquare } from 'lucide-react';

const service = new UserService();
const verificationCodeService = new VerificationCodeService();

const formatPhone = (digits?: string) => {
    if (!digits) return 'seu número';
    const match = String(digits).match(/^55(\d{2})(\d{4,5})(\d{4})$/);
    return match ? `+55 (${match[1]}) ${match[2]}-${match[3]}` : `+${digits}`;
};

type ConfiguracoesContaPhoneNumberProps = {
    // `prompt`: versao curta do aviso de primeiro acesso; o padrao e a tela de ajustes.
    variant?: 'settings' | 'prompt';
    onDismiss?: () => void;
};

export default function ConfiguracoesContaPhoneNumber({ variant = 'settings', onDismiss }: ConfiguracoesContaPhoneNumberProps) {
    const { user, changeUser } = useAuthContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [loading, setLoading] = useState(false);
    const [request, setRequest] = useState<any>(null);
    const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');

    useEffect(() => {
        setLoading(true);
        verificationCodeService
            .getByAction('changePhoneNumber')
            .then(({ data }) => {
                setRequest(data);
            })
            .catch(() => {
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // A mascara so repassa o valor ao estado no blur; ao enviar com Enter o campo ainda esta em foco.
        const number = e.currentTarget.querySelector<HTMLInputElement>('#phone')?.value || phoneNumber;

        if (!number) {
            errorSnackbar('Informe seu número de WhatsApp.');
            return;
        }

        setLoading(true);
        try {
            const { data } = await service.requestChangePhoneNumber(number);
            setRequest(data);
            setPhoneNumber('');
            successSnackbar('Enviamos o link de confirmação para o seu WhatsApp.');
        } catch (e) {
            errorSnackbar('Não foi possível enviar a confirmação. Confira o número e tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const removeRequest = async () => {
        if (!request?._id) return;

        setLoading(true);
        try {
            await verificationCodeService.delete(request?._id);
            setRequest(null);
            successSnackbar('Solicitação removida com sucesso!');
        } catch (e) {
            errorSnackbar('Não foi possível remover a solicitação.');
        } finally {
            setLoading(false);
        }
    };

    const removeNumber = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setLoading(true);
        try {
            await service.removePhoneNumber();
            setPhoneNumber('');
            setRequest(null);
            changeUser({ phoneNumber: '' });
            successSnackbar('Número removido com sucesso!');
        } catch (e) {
            console.log(e);
            errorSnackbar('Não foi possível remover o número.');
        } finally {
            setLoading(false);
        }
    };

    if (variant === 'prompt') {
        if (request) {
            return (
                <div className="text-center">
                    <div className="rounded-2xl bg-base-200 px-4 py-4 dark:bg-base-300">
                        <p className="text-sm text-base-content/65">Enviamos um link de confirmação para</p>
                        <p className="mt-1 text-lg font-semibold tabular-nums text-base-content">{formatPhone(request?.data?.phoneNumber)}</p>
                        <p className="mt-2 text-xs text-base-content/55">Abra a mensagem no WhatsApp e toque no link até as {moment(request.expiresIn).format('HH:mm')}.</p>
                    </div>
                    <button type="button" className="btn btn-primary mt-5 h-12 w-full rounded-full" onClick={onDismiss}>Entendi</button>
                    <button type="button" className="btn btn-ghost mt-1 h-11 min-h-11 w-full rounded-full text-base-content/65" onClick={removeRequest} disabled={loading}>
                        Corrigir o número
                    </button>
                </div>
            );
        }

        return (
            <form onSubmit={handleSubmit}>
                <PromptBenefits
                    items={[
                        { icon: CalendarCheck, text: 'Confirmação dos seus agendamentos' },
                        { icon: Bell, text: 'Lembretes e avisos do estabelecimento' },
                        { icon: MessageSquare, text: 'Agendamento pelo WhatsApp, quando disponível' },
                    ]}
                />
                <div className="mt-4">
                    <PhoneNumber
                        value={phoneNumber}
                        setValue={setPhoneNumber}
                        label="Seu número de WhatsApp"
                        placeholder="+55 (00) 0 0000-0000"
                        inputClassName="input input-bordered h-12 w-full rounded-xl text-base"
                    />
                    <p className="mt-2 text-xs leading-relaxed text-base-content/55">Vamos enviar um link pelo WhatsApp para confirmar que o número é seu.</p>
                </div>
                <button className="btn btn-primary mt-5 h-12 w-full rounded-full" type="submit" disabled={loading}>
                    {loading ? <span className="loading loading-spinner loading-sm" aria-label="Enviando"></span> : 'Enviar confirmação'}
                </button>
                <button type="button" className="btn btn-ghost mt-1 h-11 min-h-11 w-full rounded-full text-base-content/65" onClick={onDismiss}>
                    Agora não
                </button>
            </form>
        );
    }

    if (request) {
        return (
            <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
                <SettingsSection title="Confirmação pendente" footer="Solicitação enviada para o número informado.">
                    <SettingsRow label="Número">
                        <span className="estab-row-value">+{request?.data?.phoneNumber || 'desconhecido'}</span>
                    </SettingsRow>
                    <SettingsRow label="O link expira em">
                        <span className="estab-row-value">{moment(request.expiresIn).format('DD/MM/YYYY HH:mm')}</span>
                    </SettingsRow>
                    <SettingsRow inline>
                        <button
                            type="button"
                            className="estab-link-button estab-link-button--destructive justify-self-start"
                            onClick={removeRequest}
                            disabled={loading}
                        >
                            Remover solicitação
                        </button>
                    </SettingsRow>
                </SettingsSection>
            </div>
        );
    }

    if (!!user?.phoneNumber) {
        return (
            <form onSubmit={removeNumber} className="grid gap-6 xl:grid-cols-2 xl:items-start">
                <SettingsSection title="Número WhatsApp" footer="Usado para receber confirmações e avisos importantes.">
                    <SettingsRow label="Número">
                        <span className="estab-row-value">{user?.phoneNumber}</span>
                    </SettingsRow>
                    <SettingsRow label="Status">
                        <span className="estab-row-value !text-success">
                            <CircleCheck className="mr-1.5" aria-hidden />
                            Verificado
                        </span>
                    </SettingsRow>
                    <SettingsRow inline>
                        <button
                            className="estab-link-button estab-link-button--destructive justify-self-start"
                            type="submit"
                            disabled={loading}
                        >
                            Remover número
                        </button>
                    </SettingsRow>
                </SettingsSection>
            </form>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
                <SettingsSection
                    title="Número WhatsApp"
                    footer="Informe seu WhatsApp para receber confirmações e avisos importantes. Enviaremos um link pelo WhatsApp para confirmar o número."
                >
                    <SettingsRow label="Número" htmlFor="phone">
                        <PhoneNumber
                            value={phoneNumber}
                            setValue={setPhoneNumber}
                            label="Número de telefone"
                            hideLabel
                            placeholder="+55 (00) 0 0000-0000"
                            inputClassName="input estab-field"
                        />
                    </SettingsRow>
                </SettingsSection>
            </div>

            <div className="estab-savebar mt-6">
                <button className="btn btn-primary w-full sm:w-auto sm:min-w-[11rem]" type="submit" disabled={loading}>
                    {loading && <span className="loading loading-spinner loading-sm"></span>}
                    Continuar
                </button>
            </div>
        </form>
    );
}
